/**
 * Data hooks for §8 Activities.
 *
 * `ActivitiesPages.jsx` still reads its feed/follow-ups through `crm.api.js`'s
 * fixture-shaped `listActivities` — that screen is a separate, broader piece of
 * work. These hooks are the real contract in `activities.api.js`, first wired
 * up for the lead record page's Activity tab, scoped by `lead_id`; nothing
 * here is lead-specific, so any screen can reuse them with a different filter.
 * `CalendarPage.jsx` and the lead/account Activity tabs both do.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import * as activitiesApi from '../../api/activities.api.js';
import { queryKeys } from '../../app/queryClient.js';
import { pushToast } from '../ui/uiSlice.js';

/** @param {object} params e.g. `{ lead_id, sort: '-created_at', limit: 50 }` */
export function useActivities(params, enabled = true) {
  return useQuery({
    queryKey: queryKeys.activities.list(params),
    queryFn: () => activitiesApi.listActivities(params),
    enabled,
  });
}

/**
 * The feed's own "load older on scroll up" pagination.
 *
 * `GET /activities?sort=-created_at` walks newest-to-oldest one cursor page at
 * a time; that's exactly "scroll up reveals older" — no `prev_cursor` or
 * separate backward query needed. `FeedPage.jsx` (the full-page feed, 25 then
 * 20 a page) and `TeamFeedCard` (the Activities screen's embedded panel, 10
 * then 10 — a sidebar card has no room for 25) both flatten and reverse the
 * pages for display (oldest first, newest last, chat-style).
 *
 * @param {object} params any of `listActivitiesQuery`'s fields except `sort`/`limit`/`cursor`
 * @param {boolean} [enabled]
 * @param {{first?:number, more?:number}} [pageSize]
 */
export function useActivitiesFeed(params, enabled = true, pageSize = {}) {
  const { first = 25, more = 20 } = pageSize;
  return useInfiniteQuery({
    queryKey: queryKeys.activities.feed(params),
    queryFn: ({ pageParam }) => activitiesApi.listActivities({
      ...params,
      sort: '-created_at',
      limit: pageParam ? more : first,
      cursor: pageParam ?? undefined,
    }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => (lastPage.page?.has_more ? lastPage.page.next_cursor : undefined),
    enabled,
  });
}

/** @param {{from:string, to:string, user_ids?:string, kinds?:string}} params */
export function useActivitiesCalendar(params, enabled = true) {
  return useQuery({
    queryKey: queryKeys.activities.calendar(params),
    queryFn: () => activitiesApi.getCalendar(params),
    enabled,
  });
}

export function useCreateActivity() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (payload) => activitiesApi.createActivity(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.activities.all });
      dispatch(pushToast({ tone: 'success', message: 'Logged' }));
    },
    onError: (error) => {
      dispatch(pushToast({ tone: 'danger', message: 'Could not log that', detail: error.message }));
    },
  });
}

export function useUpdateActivity() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ id, patch, rowVersion }) => activitiesApi.updateActivity(id, patch, rowVersion),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.activities.all }),
    onError: (error) => {
      const stale = error?.code === 'stale_write';
      dispatch(pushToast({
        tone: 'danger',
        message: stale ? 'Somebody else changed this' : 'Could not save that change',
        detail: stale ? 'Refresh to pick up their version, then try again.' : error.message,
      }));
    },
  });
}

/** Log the outcome and close an activity — optionally spinning off a follow-up. */
export function useCompleteActivity() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ id, ...options }) => activitiesApi.completeActivity(id, options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.activities.all });
      dispatch(pushToast({ tone: 'success', message: 'Marked done' }));
    },
    onError: (error) => {
      const already = error?.code === 'already_completed';
      dispatch(pushToast({
        tone: 'danger',
        message: already ? 'Already marked done' : 'Could not complete that',
        detail: already ? undefined : error.message,
      }));
    },
  });
}

export function useDeleteActivity() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ id, reason, rowVersion }) => activitiesApi.deleteActivity(id, { reason, rowVersion }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.activities.all });
      dispatch(pushToast({ tone: 'success', message: 'Deleted' }));
    },
    onError: (error) => {
      const stale = error?.code === 'stale_write';
      dispatch(pushToast({
        tone: 'danger',
        message: stale ? 'Somebody else changed this' : 'Could not delete that',
        detail: stale ? 'Refresh to pick up their version, then try again.' : error.message,
      }));
    },
  });
}
